import { lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { ArrowDown, Link2, Zap } from "lucide-react";
import { useLang } from "@/context/LangContext";

// Code-split: three.js (~600KB) loads in a separate chunk, after first paint (perf)
const DownloadOrb = lazy(() => import("./DownloadOrb"));

export default function Hero() {
  const { t } = useLang();

  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* ambient neon glow behind the orb */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -end-32 top-1/4 h-[480px] w-[480px] rounded-full opacity-25 blur-3xl"
        style={{ background: "radial-gradient(circle, var(--c-neon) 0%, transparent 65%)" }}
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 lg:px-12 pt-28 pb-16">
        <div className="grid items-center gap-10 lg:grid-cols-[1.15fr_0.85fr]">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.7 }}
              className="text-sm tracking-[0.25em] text-neon font-bold mb-6 flex items-center gap-2"
            >
              <Zap className="h-4 w-4" />
              {t("hero.tag")}
            </motion.p>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.12] tracking-tight" data-testid="hero-headline">
              <span className="masked-line">
                <motion.span
                  className="block text-ice"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.35, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                >
                  {t("hero.l1")}
                </motion.span>
              </span>
              <span className="masked-line">
                <motion.span
                  className="block neon-text-gradient drop-shadow-[0_0_25px_var(--c-neon-soft)]"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                >
                  {t("hero.l2")}
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.8 }}
              className="mt-7 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl"
            >
              {t("hero.sub")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.8 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <a
                href="#downloader"
                data-testid="hero-cta-button"
                className="rounded-full bg-neon px-8 py-3.5 font-bold text-void flex items-center gap-2 neon-glow transition-transform duration-200 hover:scale-105 active:scale-95"
              >
                <Link2 className="h-4 w-4" />
                {t("hero.cta")}
              </a>
              <a
                href="#how-it-works"
                data-testid="hero-secondary-button"
                className="rounded-full border border-white/20 px-8 py-3.5 font-bold text-ice/80 transition-colors duration-200 hover:border-neon hover:text-neon active:scale-95"
              >
                {t("hero.sec")}
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto h-72 w-72 sm:h-96 sm:w-96"
          >
            <Suspense fallback={<div className="absolute inset-0" aria-hidden="true" />}>
              <DownloadOrb />
            </Suspense>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-muted-foreground"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown className="h-5 w-5" />
        </motion.div>
      </motion.div>
    </section>
  );
}
